import React from 'react';
import { PhoneIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

/**
 * Shared vocabulary for Qualifying Agent state, so the candidates table, the replay modal and
 * the screening-details panel describe a session the same way. Keeping this in one place is
 * what stops "Completed" meaning one thing in the table and another in the modal.
 *
 * WhatsApp turns are green everywhere because that is the colour people already associate with
 * WhatsApp — the channel is then readable at a glance without reading the label.
 */
export const WHATSAPP_GREEN = '#25D366';

/**
 * `tone` maps to the same badge palette the rest of the screening UI uses.
 * `hint` explains what the state means for a recruiter — shown where there is room for it.
 * `isTerminal` mirrors the server's definition in buildScreeningCandidatesList: a session that
 * can still progress on its own is not finished, no matter how far it got.
 */
export const QUALIFYING_STATUS_META = {
  pending: {
    label: 'Not started',
    tone: 'gray',
    isTerminal: false,
    hint: 'The session exists but no call has been placed yet.',
  },
  calling: {
    label: 'Calling',
    tone: 'blue',
    isTerminal: false,
    hint: 'The candidate’s phone is ringing.',
  },
  call_in_progress: {
    label: 'On call',
    tone: 'blue',
    isTerminal: false,
    hint: 'The candidate picked up and the agent is asking questions.',
  },
  call_partial: {
    label: 'Call incomplete',
    tone: 'amber',
    isTerminal: false,
    hint: 'The call ended before every question was answered. A WhatsApp follow-up is scheduled.',
  },
  call_scheduled: {
    label: 'Call scheduled',
    tone: 'blue',
    isTerminal: false,
    hint: 'The agent will call at the time shown, within the screening’s call hours — after a missed or unfinished call, or for an invite sent outside them. “In queue” means the call is due and rings as soon as a line is free (20 calls run at a time).',
  },
  whatsapp_pending: {
    label: 'WhatsApp sent',
    tone: 'green',
    isTerminal: false,
    hint: 'A WhatsApp message went out and we are waiting for the candidate’s first reply.',
  },
  whatsapp_in_progress: {
    label: 'On WhatsApp',
    tone: 'green',
    isTerminal: false,
    hint: 'The candidate is answering the remaining questions on WhatsApp.',
  },
  whatsapp_partial: {
    label: 'WhatsApp incomplete',
    tone: 'amber',
    isTerminal: false,
    hint: 'The candidate went quiet on WhatsApp. A follow-up call is scheduled.',
  },
  completed: {
    label: 'Completed',
    tone: 'green',
    isTerminal: true,
    hint: 'Every question was answered.',
  },
  declined: {
    label: 'Declined',
    tone: 'red',
    isTerminal: true,
    hint: 'The candidate said they did not want to continue.',
  },
  abandoned: {
    label: 'No response',
    tone: 'gray',
    isTerminal: true,
    hint: 'Neither the call nor WhatsApp got a response. Nothing further is scheduled.',
  },
};

const TONE_CLASSES = {
  gray: 'bg-gray-100 text-gray-600 border-gray-200',
  blue: 'bg-[#007BFF]/10 text-[#0062CC] border-[#007BFF]/20',
  green: 'bg-green-50 text-green-700 border-green-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

// The status dot in the table cell, in the same tones.
const DOT_CLASSES = {
  gray: 'bg-gray-400',
  blue: 'bg-[#007BFF]',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

export function getQualifyingStatusMeta(status) {
  return (
    QUALIFYING_STATUS_META[status] || {
      label: status || 'Unknown',
      tone: 'gray',
      isTerminal: false,
      hint: '',
    }
  );
}

/**
 * The status hint for this session, made true for it: a completed session where some questions
 * went unanswered must not say "Every question was answered." (14 Oct: it did, with the Go Colors
 * question never answered). `answeredCount` comes from the server's answer review.
 */
export function qualifyingHint(qualifying) {
  const meta = getQualifyingStatusMeta(qualifying?.status);
  const total = qualifying?.totalQuestions ?? 0;
  const answered = qualifying?.answeredCount ?? total;
  if (qualifying?.status === 'completed' && total > 0 && answered < total) {
    const missing = total - answered;
    return `The call finished, but ${missing} question${missing === 1 ? ' was' : 's were'} not answered.`;
  }
  return meta.hint;
}

// Scoring runs once the call's post-call transcript is ready — usually a minute or two after a
// call completes, at most the server's FINALIZE_WAIT_MAX_MS (8 min, qualifying-agent.service.js)
// — and the server retries a failed attempt every couple of minutes. A completed session without
// a score this soon after finishing is "being scored", not "unscored" — past this window it is
// shown as unscored. Keep it above FINALIZE_WAIT_MAX_MS.
export const SCORE_PENDING_WINDOW_MS = 15 * 60 * 1000;

/** True while a completed session's score is still expected to land. */
export function isScorePending(qualifying, now = Date.now()) {
  if (!qualifying || qualifying.status !== 'completed') return false;
  if (qualifying.overallScore != null || qualifying.scoredAt) return false;
  const completedAt = Date.parse(qualifying.completedAt || '');
  return Number.isFinite(completedAt) && now - completedAt < SCORE_PENDING_WINDOW_MS;
}

// A call that is ringing or running.
const LIVE_CALL_STATUSES = new Set(['calling', 'call_in_progress']);

/**
 * True while a session can still change by itself: a call or WhatsApp step is running or due,
 * or the score of a call that just finished has not arrived yet.
 *
 * Nothing re-reads a session on a timer. Until 22 Oct the table and the replay modal each
 * polled every 5 s while this held, which kept Railway and Mongo busy for a screen nobody
 * watches live. Now this only decides two things: whether a Refresh button is worth showing,
 * and whether closing a session's view should re-read the table's summary once.
 */
export function qualifyingCanStillChange(qualifying, now = Date.now()) {
  if (!qualifying) return false;
  return !getQualifyingStatusMeta(qualifying.status).isTerminal || isScorePending(qualifying, now);
}

export function QualifyingStatusBadge({ status, className = '' }) {
  const meta = getQualifyingStatusMeta(status);
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${TONE_CLASSES[meta.tone]} ${className}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * Colour bands for the qualifying fit score. Deliberately the same three-band red/amber/green
 * split the rest of the screening UI uses for scores, so a recruiter scanning a mixed table
 * does not have to learn a second colour language for this column.
 */
export function getQualifyingScoreTone(score) {
  if (score == null) return 'gray';
  if (score >= 70) return 'green';
  if (score >= 40) return 'amber';
  return 'red';
}

const SCORE_TEXT_CLASSES = {
  gray: 'text-gray-400',
  green: 'text-green-700',
  amber: 'text-amber-700',
  red: 'text-red-700',
};

/**
 * The score for a whole qualifying conversation, or a dash when there isn't one.
 *
 * A dash is a real and common state — a session that ended early is never scored, and neither
 * is one completed while the scoring model was unreachable. It must not read as "scored zero",
 * which is why the dash is muted and carries its own tooltip.
 */
export function QualifyingScore({ score, summary, className = '' }) {
  const tone = getQualifyingScoreTone(score);
  const hasScore = score != null;
  return (
    <span
      className={`text-sm font-bold tabular-nums ${SCORE_TEXT_CLASSES[tone]} ${className}`}
      title={
        hasScore
          ? summary || `Qualifying fit score: ${score}%`
          : 'Not scored — a score is only produced once every question has been answered.'
      }
    >
      {hasScore ? `${score}%` : '—'}
    </span>
  );
}

/**
 * The verdict, in the same five words the AI interview uses — `qualifyingScore.js` maps the
 * score to these at the same 75/55/35 thresholds, and REJECT only ever comes from the knockout
 * gate. Kept here rather than derived in the cell so the table, the replay modal and the
 * details panel cannot end up phrasing the same verdict differently.
 */
export const RECOMMENDATION_META = {
  ADVANCE: { label: 'Advance', tone: 'green' },
  CONSIDER_TO_ADVANCE: { label: 'Consider', tone: 'green' },
  REVIEW_REQUIRED: { label: 'Review', tone: 'amber' },
  DO_NOT_ADVANCE: { label: 'Do not advance', tone: 'red' },
  REJECT: { label: 'Not interested', tone: 'red' },
};

/**
 * Why REJECT reads as "Not interested" rather than "Rejected": on this stage the gate only ever
 * fires because the candidate themselves said no — to the role, or to sitting the next step.
 * "Rejected" would imply we made that decision, which would be wrong in front of a recruiter
 * deciding who to call back.
 */
export function getRecommendationMeta(recommendation) {
  return RECOMMENDATION_META[recommendation] || null;
}

// A knockout on one of these is the candidate saying no. A knockout on a recruiter's own question
// (notice period, salary, experience) is a requirement they did not meet — they never said they
// were not interested (28 Oct: a 70-day notice period read "Not interested" in red everywhere).
const SAID_NO_KINDS = new Set(['intro', 'willingness', 'ai_interview_consent']);

// Red like "Not interested" (29 Oct): either way the candidate is out, and the row must say so.
export const REQUIREMENT_NOT_MET_META = { label: 'Requirement not met', tone: 'red' };

/**
 * Why a candidate is out, or null: 'not_interested' (declined, or said no to the interest or
 * consent question) or 'requirement' (failed a knockout on a recruiter's own question). A knockout
 * with no kind is from before kinds were stored and keeps the old "not interested" reading.
 */
export function ruledOutReason(qualifying) {
  if (!qualifying) return null;
  if (qualifying.status === 'declined' || qualifying.notInterested) return 'not_interested';
  // The call ended on a recruiter's knockout: known before the score lands.
  if (qualifying.requirementNotMet) return 'requirement';
  const { knockout } = qualifying;
  if (knockout) return knockout.kind && !SAID_NO_KINDS.has(knockout.kind) ? 'requirement' : 'not_interested';
  return qualifying.recommendation === 'REJECT' ? 'not_interested' : null;
}

/** The verdict to show for a summary or session: the score's word unless a knockout overrides it. */
export function getVerdictMeta(qualifying) {
  const reason = ruledOutReason(qualifying);
  if (reason === 'not_interested') return RECOMMENDATION_META.REJECT;
  if (reason === 'requirement') return REQUIREMENT_NOT_MET_META;
  return getRecommendationMeta(qualifying?.recommendation);
}

const VERDICT_CLASSES = {
  green: 'text-green-700',
  amber: 'text-amber-700',
  red: 'text-red-600',
  gray: 'text-gray-500',
};

/**
 * The whole "Qualifying" cell, in one place.
 *
 * The rule it follows is one primary value per cell. The old cell showed five competing things
 * at once — score, status badge, "5/5 answered", and a chip per channel — so nothing read as
 * the headline and a recruiter scanning sixty rows had to parse each one. Everything that is
 * not the verdict now lives in the tooltip and in the replay modal, which already shows turns,
 * channels and the per-answer breakdown.
 *
 * What it shows, in order of what the recruiter actually needs to know:
 *
 *   still running      the status badge alone. There is no score yet and inventing a placeholder
 *                      for one is how a half-finished call gets acted on.
 *   finished + scored  the score, large, with the verdict under it.
 *   knocked out        the score stays visible but muted, with "Not interested" as the verdict —
 *                      an 84% who declined is the person to re-approach, not a 0.
 *   finished, scoring  "Scoring…" for the few seconds (at most SCORE_PENDING_WINDOW_MS) before
 *                      the score lands — the table refreshes itself meanwhile.
 *   finished, unscored a dash and the status. A dash is a real state, not a zero.
 */
/**
 * When the next call is due, in India time — the time alone when it is today ("4:30 PM"), with
 * the day otherwise ("Tue, 9:05 AM"). Call hours are India time, so the label is too.
 */
export function formatNextCall(iso, now = new Date()) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const day = (d) => d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  const time = at.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (day(at) === day(now)) return time;
  const weekday = at.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short' });
  return `${weekday}, ${time}`;
}

/**
 * How one call ended, by the server's `outcome` (callSchedule.js describeCallAttempt). Picked-up
 * endings are amber or blue so they never read like "nobody answered" (21 Oct: "Call scheduled"
 * on every row made a recruiter think none of 33 candidates had picked up — 13 had talked).
 * A hang-up is amber, not red: red stays for a candidate who said no.
 */
export const CALL_OUTCOME_META = {
  completed: { label: 'Completed', tone: 'green' },
  declined: { label: 'Declined', tone: 'red' },
  candidate_busy: { label: 'Asked to call later', tone: 'blue' },
  candidate_hung_up: { label: 'Candidate hung up', tone: 'amber' },
  call_dropped: { label: 'Call dropped', tone: 'amber' },
  ended_early: { label: 'Call ended early', tone: 'amber' },
  no_answer: { label: 'No answer', tone: 'gray' },
  line_busy: { label: 'Line busy', tone: 'gray' },
  rejected: { label: 'Call rejected', tone: 'gray' },
  unreachable: { label: 'Unreachable', tone: 'gray' },
  voicemail: { label: 'Voicemail', tone: 'gray' },
  not_connected: { label: 'Could not connect', tone: 'gray' },
  invalid_number: { label: 'Invalid number', tone: 'red' },
};

const OUTCOME_TEXT_CLASSES = {
  gray: 'text-gray-500',
  blue: 'text-[#0062CC]',
  green: 'text-green-700',
  amber: 'text-amber-700',
  red: 'text-red-600',
};

export function getCallOutcomeMeta(outcome) {
  return CALL_OUTCOME_META[outcome] || { label: 'Call ended', tone: 'gray' };
}

/**
 * "Candidate hung up · 5/8 answered" for a call that was picked up; the outcome alone otherwise.
 * `totalQuestions` from the session; '' when there is no call.
 */
export function callOutcomeText(call, totalQuestions) {
  if (!call) return '';
  const { label } = getCallOutcomeMeta(call.outcome);
  const showsCount = call.pickedUp && call.answeredCount != null && totalQuestions > 0 && call.outcome !== 'completed';
  return showsCount ? `${label} · ${call.answeredCount}/${totalQuestions} answered` : label;
}

/** The text colour for a call outcome, in the badge palette. */
export function callOutcomeTextClass(outcome) {
  return OUTCOME_TEXT_CLASSES[getCallOutcomeMeta(outcome).tone];
}

export function CallOutcomeBadge({ outcome, className = '' }) {
  const meta = getCallOutcomeMeta(outcome);
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE_CLASSES[meta.tone]} ${className}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * "Call 2 of 3 · 4:30 PM" for a scheduled call; "Call-back they asked for · 4:30 PM" when the
 * candidate asked to be called back (summary `callBack`); '' otherwise.
 */
export function scheduledCallLabel(qualifying, now = new Date()) {
  if (!qualifying || qualifying.status !== 'call_scheduled' || !qualifying.nextCallAt) return '';
  // Due already: queued behind the calls on the line (the dialer rings 20 at a time).
  const when = new Date(qualifying.nextCallAt) <= now ? 'in queue' : formatNextCall(qualifying.nextCallAt, now);
  // Not "call N of M": a call-back they asked for can come after every attempt was used.
  if (qualifying.callBack) return `Call-back they asked for · ${when}`;
  const next = (qualifying.callAttempts || 0) + 1;
  const max = qualifying.maxCallAttempts || next;
  return `Call ${Math.min(next, max)} of ${max} · ${when}`;
}

/**
 * Why the next call is booked, in a recruiter's words (server qualifyingSummary nextCallFor):
 * "They asked to be called back", "4 questions still unanswered", or, with questions marked
 * mandatory, "1 mandatory question still unanswered". '' when no call is booked.
 */
export function nextCallReasonLabel(qualifying) {
  const why = qualifying?.nextCallFor;
  if (!why || !qualifying.nextCallAt) return '';
  if (why.reason === 'call_back') return 'They asked to be called back';
  const open = Number(why.open) || 0;
  if (!open) return '';
  const kind = why.reason === 'mandatory_open' ? 'mandatory question' : 'question';
  return `${open} ${kind}${open === 1 ? '' : 's'} still unanswered`;
}

export function QualifyingCell({ qualifying, onOpen }) {
  // No session at all: the candidate is in the round but has never been called. Saying so beats
  // an ambiguous dash, and it is the state a recruiter acts on by sending the invite.
  if (!qualifying) {
    return <span className="text-xs text-gray-400">Not started</span>;
  }

  const meta = getQualifyingStatusMeta(qualifying.status);
  // The call's own "not interested" stands in for the score's REJECT until (or unless) it lands.
  // A requirement not met shows why under the score ("Notice period exceeds 31 days"), so the
  // table says what happened without opening the card (29 Oct; it read "Completed" before).
  const ruledOut = ruledOutReason(qualifying);
  const verdict =
    ruledOut === 'requirement'
      ? { label: qualifying.knockout?.note?.replace(/\.$/, '') || 'Knocked out', tone: 'red' }
      : getVerdictMeta(qualifying);
  const hasScore = qualifying.overallScore != null;
  const knockedOut = !!qualifying.knockout || !!qualifying.notInterested;
  const saidNo = ruledOut === 'not_interested';
  const scoring = !hasScore && !knockedOut && isScorePending(qualifying);
  const canOpen = typeof onOpen === 'function';
  const scheduledLabel = scheduledCallLabel(qualifying);
  // How the last call ended, while waiting for the next step — not while a call is live.
  const live = LIVE_CALL_STATUSES.has(qualifying.status);
  const lastCallText = live ? '' : callOutcomeText(qualifying.lastCall, qualifying.totalQuestions);
  // The cell's second line is one fact. A call they picked up says how it ended ("Asked to call
  // later"); otherwise the next call, if one is booked; otherwise how the last call ended. The
  // answered count is left out: the Status column beside it already shows "5/8" (23 Oct).
  const lastOutcome = live || !qualifying.lastCall ? '' : getCallOutcomeMeta(qualifying.lastCall.outcome).label;
  const subLine =
    qualifying.lastCall?.pickedUp && lastOutcome
      ? lastOutcome
      : scheduledLabel || (lastOutcome ? `Last call: ${lastOutcome.toLowerCase()}` : '');

  const tooltip = [
    lastCallText ? `Last call: ${lastCallText}` : '',
    scheduledLabel ? `Next: ${scheduledLabel} IST` : '',
    nextCallReasonLabel(qualifying) ? `Why: ${nextCallReasonLabel(qualifying)}` : '',
    qualifying.callBack?.heard ? `They said: “${qualifying.callBack.heard}”` : '',
    qualifying.callAttempts ? `${qualifying.callAttempts} of ${qualifying.maxCallAttempts} call attempt${qualifying.maxCallAttempts === 1 ? '' : 's'} used` : '',
    qualifying.scoreSummary,
    `${qualifying.answeredCount}/${qualifying.totalQuestions} questions answered`,
    `${qualifying.callTurns} call turn${qualifying.callTurns === 1 ? '' : 's'}, ${qualifying.whatsappTurns} WhatsApp turn${qualifying.whatsappTurns === 1 ? '' : 's'}`,
    ruledOut && qualifying.knockout?.note ? `Ruled out: ${qualifying.knockout.note}` : '',
    qualifyingHint(qualifying),
    'Click to open the transcript.',
  ]
    .filter(Boolean)
    .join('\n');

  // A score band and a verdict must never contradict each other, so the number takes its colour
  // from the verdict whenever there is one.
  const scoreToneClass = saidNo
    ? 'text-gray-400'
    : SCORE_TEXT_CLASSES[getQualifyingScoreTone(qualifying.overallScore)];

  return (
    <button
      type="button"
      data-testid="qualifying-session-view"
      disabled={!canOpen}
      onClick={() => canOpen && onOpen()}
      title={tooltip}
      className="mx-auto flex min-w-[84px] flex-col items-center gap-0.5 rounded-lg px-2 py-1 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#007BFF] disabled:cursor-default disabled:hover:bg-transparent"
    >
      {meta.isTerminal ? (
        <>
          <span className={`text-[17px] font-bold leading-none tabular-nums ${scoreToneClass}`}>
            {hasScore ? `${qualifying.overallScore}%` : scoring ? '…' : '—'}
          </span>
          <span
            className={`max-w-[140px] truncate text-[11px] font-semibold leading-tight ${
              verdict ? VERDICT_CLASSES[verdict.tone] : VERDICT_CLASSES.gray
            }`}
          >
            {verdict ? verdict.label : scoring ? 'Scoring…' : meta.label}
          </span>
        </>
      ) : (
        // A dot and a plain label, not a pill: the Status column beside it is already a pill, and
        // two coloured pills per row was the noise (23 Oct).
        <>
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold leading-tight text-gray-800">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_CLASSES[meta.tone]}`} />
            {meta.label}
          </span>
          {subLine && (
            <span
              data-testid="qualifying-last-call"
              className="text-[11px] leading-tight text-gray-500 tabular-nums"
            >
              {subLine}
            </span>
          )}
        </>
      )}
    </button>
  );
}

/**
 * A small channel marker. `count` renders the number of turns captured on that channel;
 * omitting it renders the channel name alone (used inline in the transcript).
 */
export function ChannelChip({ channel, count, prefix }) {
  const isWhatsApp = channel === 'whatsapp';
  const Icon = isWhatsApp ? ChatBubbleLeftRightIcon : PhoneIcon;
  const name = isWhatsApp ? 'WhatsApp' : 'Phone call';

  // Zero-count chips stay visible but muted, so "nothing happened on this channel" reads
  // differently from "this channel was never part of the session".
  const muted = count === 0;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold"
      style={
        muted
          ? { backgroundColor: '#F9FAFB', borderColor: '#F3F4F6', color: '#9CA3AF' }
          : isWhatsApp
            ? { backgroundColor: '#F0FDF4', borderColor: '#BBF7D0', color: '#15803D' }
            : { backgroundColor: 'rgba(0,123,255,0.06)', borderColor: 'rgba(0,123,255,0.2)', color: '#0062CC' }
      }
      title={count == null ? name : `${count} ${name.toLowerCase()} turn${count === 1 ? '' : 's'}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {prefix ? `${prefix} ${name}` : name}
      {count != null && <span className="font-extrabold">{count}</span>}
    </span>
  );
}

/**
 * Where a candidate stands in a screening whose only stage is the Qualifying Agent, in the five
 * words the Status column uses there (22 Oct: the column said "In Progress" for a candidate who
 * had never picked up and for one halfway through the questions alike). Finer detail — which
 * call, when the next one is — stays in the Qualifying Call column beside it.
 *
 *   invited         a session exists but nothing has gone out yet
 *   contacting      called or messaged; the candidate has not engaged
 *   in_progress     they picked up, replied, answered something, or asked to be called back
 *   completed       "Interested": said yes and finished (every question, or every mandatory one)
 *   knocked_out     failed a recruiter's knockout question (requirement not met — ruledOutReason)
 *   not_interested  declined, or said no to the interest / consent question
 *   no_response     every call and message went unanswered; nothing more is scheduled
 */
export const QUALIFYING_STAGE_META = {
  invited: { label: 'Invited', tone: 'gray', hint: 'Nothing has been sent to the candidate yet.' },
  contacting: { label: 'Contacting', tone: 'blue', hint: 'We have called or messaged; the candidate has not replied yet.' },
  in_progress: { label: 'In progress', tone: 'amber', hint: 'The candidate is answering the questions.' },
  completed: { label: 'Interested', tone: 'green', hint: 'The candidate said they are interested and answered the questions.' },
  knocked_out: { label: 'Knocked out', tone: 'red', hint: 'The candidate did not meet a knockout requirement.' },
  not_interested: { label: 'Not interested', tone: 'red', hint: 'The candidate said they are not interested.' },
  no_response: { label: 'No response', tone: 'gray', hint: 'The candidate never replied to the calls or messages.' },
};

const ENGAGED_STATUSES = new Set(['call_in_progress', 'whatsapp_in_progress', 'call_partial', 'whatsapp_partial']);

/** The stage key for a candidate's qualifying summary (the table row's `qualifying`). */
export function qualifyingStage(qualifying) {
  if (!qualifying) return 'invited';
  const { status } = qualifying;
  const ruledOut = ruledOutReason(qualifying);
  if (ruledOut === 'not_interested') return 'not_interested';
  if (ruledOut === 'requirement') return 'knocked_out';
  if (status === 'completed') return 'completed';
  if (status === 'abandoned') return 'no_response';
  if (status === 'pending') return 'invited';
  const engaged =
    ENGAGED_STATUSES.has(status) ||
    (qualifying.answeredCount || 0) > 0 ||
    Boolean(qualifying.lastCall?.pickedUp) ||
    Boolean(qualifying.callBack);
  return engaged ? 'in_progress' : 'contacting';
}

export function getQualifyingStageMeta(qualifying) {
  return QUALIFYING_STAGE_META[qualifyingStage(qualifying)];
}

/** The Status chip for a qualifying-only screening. A button when `onOpen` is given. */
export function QualifyingStageChip({ qualifying, onOpen, className = '' }) {
  const meta = getQualifyingStageMeta(qualifying);
  const total = qualifying?.totalQuestions || 0;
  const answered = qualifying?.answeredCount || 0;
  const title = [meta.hint, total ? `${answered} of ${total} questions answered` : '', onOpen ? 'Click to see the answers.' : '']
    .filter(Boolean)
    .join('\n');
  const classes = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TONE_CLASSES[meta.tone]} ${className}`;
  if (typeof onOpen !== 'function') {
    return (
      <span className={classes} title={title}>
        {meta.label}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid="qualifying-stage-chip"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      title={title}
      className={`${classes} cursor-pointer hover:brightness-95 focus:outline-none focus:ring-1 focus:ring-[#007BFF]`}
    >
      {meta.label}
      {total > 0 && <span className="font-medium opacity-75 tabular-nums">· {answered}/{total}</span>}
    </button>
  );
}
